/**
 * Pure helpers for PumpIO top movers ranking and quote merge.
 * Live % change and price come from Finnhub (worker payload: change, price).
 */

/** Merge Finnhub quote updates into a tree clone (mutates node). */
export function mergeChanges(node, updates) {
  if (!node.children) {
    const ticker = node.name.split('\n')[0].trim();
    const quote = updates[ticker];
    if (quote) {
      if (quote.change !== undefined) node.change = quote.change;
      if (quote.price !== undefined && quote.price !== null) node.price = quote.price;
    }
  } else {
    node.children.forEach((child) => mergeChanges(child, updates));
  }
}

/**
 * Collect unique leaf tickers with change (and optional price).
 * First occurrence wins when the same ticker appears in multiple sectors.
 */
export function collectMovers(node, seen = new Map()) {
  if (!node.children) {
    const ticker = node.name.split('\n')[0].trim();
    if (node.change !== undefined && !seen.has(ticker)) {
      const entry = { name: ticker, change: node.change };
      if (node.price !== undefined && node.price !== null) entry.price = node.price;
      seen.set(ticker, entry);
    }
  } else {
    node.children.forEach((child) => collectMovers(child, seen));
  }
  return [...seen.values()];
}

/**
 * Split movers into top gainers (desc by %) and top losers (asc by %, most negative first).
 * Groups are non-overlapping: only change > 0 for gainers, change < 0 for losers.
 */
export function rankGainersAndLosers(movers, limit = 5) {
  const withChange = movers.filter((m) => typeof m.change === 'number' && !Number.isNaN(m.change));
  const gainers = [...withChange]
    .filter((m) => m.change > 0)
    .sort((a, b) => b.change - a.change)
    .slice(0, limit);
  const losers = [...withChange]
    .filter((m) => m.change < 0)
    .sort((a, b) => a.change - b.change)
    .slice(0, limit);
  return { gainers, losers };
}

/** Format USD price for mover rows; null/undefined → null (UI omits price). */
export function formatMoverPrice(price) {
  if (price === undefined || price === null || Number.isNaN(Number(price))) return null;
  const n = Number(price);
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
  return `$${n.toFixed(2)}`;
}

/** Format percent change with sign for display. */
export function formatMoverChange(change) {
  if (typeof change !== 'number' || Number.isNaN(change)) return '—';
  const sign = change > 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}
